var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useMemo } from "react";
import * as Styled from "./cup-animation-2d.style";
import { selectors, useAppSelector } from "store";
import { useChartTimeline } from "ui/components/elements/chart/chart-timeline";
import { useCurrentVacuumLevel } from "hooks/graph-panel/use-current-vacuum-level";
import { useCurrentAirConsumption } from "hooks/graph-panel/use-current-air-consumption";
var ARM_X = 60;
var ARM_END_Y = 10;
var CUP_SCALE = 0.4;
var CUP_NATURAL_WIDTH = 160;
var CUP_NATURAL_HEIGHT = 90;
var CUP_HEIGHT = CUP_NATURAL_HEIGHT * CUP_SCALE;
var OBJECT_Y = 87;
var OBJECT_HEIGHT = 20;
var OBJECT_WIDTH = 100;
var ATTACH_JUMP = 4;
var MAX_DROP = OBJECT_Y - ARM_END_Y - CUP_HEIGHT;
var COLLAPSE_AMOUNT = 0.4;
var AIR_PARTICLE_COUNT = 3;
var AIR_SPREAD_PARTICLE_COUNT = 1;
var AIR_SPREAD = 22;
var CUP_HALF_WIDTH = (CUP_NATURAL_WIDTH * CUP_SCALE) / 2;
// CUP_PATH: 160×90, mount at top-center, wide flat mouth at bottom
var CUP_PATH = "M 58 4 Q 58 0 62 0 L 98 0 Q 102 0 102 4 L 102 32 C 94 50 148 74 160 90 L 0 90 C 12 74 66 50 58 32 Z";
var lerp = function (t, from, to) {
    if (to <= from)
        return t >= to ? 1 : 0;
    return Math.max(0, Math.min(1, (t - from) / (to - from)));
};
var interpolateColor = function (t, from, to) {
    var clamped = Math.max(0, Math.min(1, t));
    var _a = __read(from, 3), r1 = _a[0], g1 = _a[1], b1 = _a[2];
    var _b = __read(to, 3), r2 = _b[0], g2 = _b[1], b2 = _b[2];
    var r = Math.round(r1 + (r2 - r1) * clamped);
    var g = Math.round(g1 + (g2 - g1) * clamped);
    var b = Math.round(b1 + (b2 - b1) * clamped);
    return "rgb(".concat(r, ", ").concat(g, ", ").concat(b, ")");
};
var CUP_COLORS = {
    grey: { atATM: [210, 210, 210], atFullVacuum: [130, 130, 130] },
    red: { atATM: [240, 78, 53], atFullVacuum: [150, 49, 33] },
    darkRed: { atATM: [199, 21, 21], atFullVacuum: [124, 13, 13] },
    blue: { atATM: [180, 214, 253], atFullVacuum: [47, 128, 237] },
    pink: { atATM: [199, 21, 21], atFullVacuum: [255, 225, 225] },
    cyan: { atATM: [200, 237, 250], atFullVacuum: [86, 204, 242] },
};
export var CupAnimation2D = function () {
    var timeline = useChartTimeline();
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var parsedGraphData = useAppSelector(selectors.selectParsedGraphData);
    var vacuumLevel = useCurrentVacuumLevel();
    var currentAirConsumption = useCurrentAirConsumption();
    var time = timeline ? playLocationPercent * timeline.totalTime : 0;
    var _a = useMemo(function () {
        if (!timeline)
            return { cupY: 0, collapse: 0, airFlow: "none" };
        var isBlowOffOnBeforePick = time > timeline.blowOffOnBeforePick.begin &&
            time < timeline.blowOffOnBeforePick.end;
        var isVacuumOnBeforePick = time > timeline.vacuumOnBeforePick.begin &&
            time < timeline.vacuumOnBeforePick.end;
        // Descent: 0 → timeToPartSecured.begin
        if (time < timeline.timeToPartSecured.begin) {
            var cupY_1 = lerp(time, 0, timeline.timeToPartSecured.begin);
            if (isBlowOffOnBeforePick) {
                return { cupY: cupY_1, collapse: 0, airFlow: "out", straightFlow: true };
            }
            if (isVacuumOnBeforePick) {
                return { cupY: cupY_1, collapse: 0, airFlow: "in", bothFlows: true };
            }
            return { cupY: cupY_1, collapse: 0, airFlow: "none" };
        }
        // Gripping: timeToPartSecured phase (PS period), collapse 0→1, vacuum building
        if (time < timeline.timeToPartSecured.end) {
            var collapseProgress = lerp(time, timeline.timeToPartSecured.begin, timeline.timeToPartSecured.end);
            return {
                cupY: 1,
                collapse: collapseProgress,
                airFlowSpeed: 2,
                airFlow: "in",
                straightFlow: true,
            };
        }
        // Transfer: cup stays down, fully gripping
        if (time < timeline.transfer.end) {
            return { cupY: 1, collapse: 1, airFlow: "none" };
        }
        // Placing: release, collapse 1→0, blow-off downward
        if (time < timeline.releaseTimeToPlacePart.end) {
            var expandProgress = lerp(time, timeline.releaseTimeToPlacePart.begin, timeline.releaseTimeToPlacePart.end);
            return {
                cupY: 1,
                collapse: 1 - expandProgress,
                airFlow: "out",
                straightFlow: true,
            };
        }
        // Return: cupY 1→0
        if (time < timeline.returnTime.end) {
            var isAdditionalRelease = time < timeline.additionalReleaseTime.end;
            return {
                cupY: 1 - lerp(time, timeline.returnTime.begin, timeline.returnTime.end),
                collapse: 0,
                airFlow: isAdditionalRelease ? "out" : "none",
                bothFlows: isAdditionalRelease,
            };
        }
        return { cupY: 0, collapse: 0, airFlow: "none" };
    }, [time, timeline]), cupY = _a.cupY, collapse = _a.collapse, airFlow = _a.airFlow, airFlowSpeed = _a.airFlowSpeed, straightFlow = _a.straightFlow, bothFlows = _a.bothFlows;
    var nonEsVacuumLevelDomain = useAppSelector(selectors.selectNonEsVacuumLevelDomain);
    var vacuumFillT = useMemo(function () {
        if (vacuumLevel === undefined)
            return 0;
        var domain = nonEsVacuumLevelDomain !== null && nonEsVacuumLevelDomain !== void 0 ? nonEsVacuumLevelDomain : parsedGraphData === null || parsedGraphData === void 0 ? void 0 : parsedGraphData.vacuumLevelDomain;
        if (!domain)
            return 0;
        var minVacuum = domain[0]; // most negative = highest vacuum
        if (minVacuum >= 0)
            return 0;
        return Math.max(0, Math.min(1, vacuumLevel / minVacuum));
    }, [vacuumLevel, nonEsVacuumLevelDomain, parsedGraphData]);
    var colorRange = useMemo(function () {
        if (!timeline)
            return CUP_COLORS.grey;
        var vacuumOnBeforePick = timeline.vacuumOnBeforePick, blowOffOnBeforePick = timeline.blowOffOnBeforePick, additionalReleaseTime = timeline.additionalReleaseTime;
        if (vacuumOnBeforePick.begin < vacuumOnBeforePick.end &&
            time >= vacuumOnBeforePick.begin &&
            time < vacuumOnBeforePick.end) {
            return CUP_COLORS.cyan;
        }
        if (time >= blowOffOnBeforePick.begin && time < blowOffOnBeforePick.end) {
            return CUP_COLORS.red;
        }
        if (time >= additionalReleaseTime.begin &&
            time < additionalReleaseTime.end) {
            return CUP_COLORS.darkRed;
        }
        if (time >= timeline.timeToPartSecured.begin) {
            var releaseStart = timeline.releaseTimeToPlacePart.begin;
            var releaseEnd = timeline.releaseTimeToPlacePart.end;
            if (time >= releaseStart && time < releaseEnd) {
                return CUP_COLORS.pink;
            }
            if (time < releaseStart) {
                return CUP_COLORS.blue;
            }
        }
        return CUP_COLORS.grey;
    }, [time, timeline]);
    var airFlowRatio = useMemo(function () {
        if (currentAirConsumption === undefined ||
            !parsedGraphData ||
            parsedGraphData.airConsumptionDomain[1] <= 0)
            return 1;
        return Math.max(0, Math.min(1, currentAirConsumption / parsedGraphData.airConsumptionDomain[1]));
    }, [currentAirConsumption, parsedGraphData]);
    var visibleArrowCount = Math.max(1, Math.round(AIR_PARTICLE_COUNT * airFlowRatio));
    var isAttached = timeline != null &&
        time >= timeline.timeToPartSecured.begin &&
        time < timeline.releaseTimeToPlacePart.end;
    var cupGroupY = ARM_END_Y + cupY * MAX_DROP + (isAttached ? ATTACH_JUMP : 0);
    var scaleY = 1 - collapse * COLLAPSE_AMOUNT;
    var cupFill = interpolateColor(vacuumFillT, colorRange.atATM, colorRange.atFullVacuum);
    return (_jsx(Styled.Wrapper, { children: _jsxs("svg", { viewBox: "0 0 120 120", width: "100%", height: "100%", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("g", { transform: "translate(".concat(ARM_X, ", ").concat(cupGroupY, ")"), children: _jsx("g", { transform: "translate(0, ".concat(CUP_HEIGHT, ") scale(1, ").concat(scaleY, ") translate(0, ").concat(-CUP_HEIGHT, ")"), children: _jsxs("g", { transform: "scale(".concat(CUP_SCALE, ") translate(").concat(-CUP_NATURAL_WIDTH / 2, ", 0)"), children: [_jsx("path", { d: CUP_PATH, fill: cupFill, stroke: "white", strokeWidth: 5, strokeLinejoin: "round" }), _jsx("rect", { x: 58, y: 0, width: 44, height: 32, rx: 4, fill: "url(#mountGradient)" })] }) }) }), airFlow !== "none" && (_jsxs(_Fragment, { children: [(straightFlow || bothFlows) &&
                            __spreadArray([], __read(Array(visibleArrowCount)), false).map(function (_, i) {
                                var speed = 2 * (airFlowSpeed !== null && airFlowSpeed !== void 0 ? airFlowSpeed : 1);
                                var t = (time * speed + i / AIR_PARTICLE_COUNT) % 1;
                                var opacity = Math.sin(t * Math.PI);
                                var cupMouthY = cupGroupY + CUP_HEIGHT;
                                var STRAIGHT_OVERSHOOT = 6;
                                var tDir = airFlow === "in" ? 1 - t : t;
                                var cy = cupGroupY -
                                    STRAIGHT_OVERSHOOT +
                                    tDir * (cupMouthY - cupGroupY + 2 * STRAIGHT_OVERSHOOT);
                                var arrowRotation = airFlow === "in" ? -90 : 90;
                                return (_jsx("g", { transform: "translate(".concat(ARM_X, ", ").concat(cy, ") rotate(").concat(arrowRotation, ")"), opacity: opacity, children: _jsx("polygon", { points: "4,0 -2,-2 -2,2", fill: "white" }) }, "straight-".concat(i)));
                            }), (!straightFlow || bothFlows) &&
                            __spreadArray([], __read(Array(AIR_SPREAD_PARTICLE_COUNT)), false).map(function (_, i) {
                                var speed = 2 * (airFlowSpeed !== null && airFlowSpeed !== void 0 ? airFlowSpeed : 1);
                                var t = (time * speed + i / AIR_PARTICLE_COUNT) % 1;
                                var opacity = Math.sin(t * Math.PI);
                                var cupMouthY = cupGroupY + CUP_HEIGHT;
                                var travelFactor = airFlow === "out" ? t : 1 - t;
                                var sides = [
                                    { rimX: ARM_X - CUP_HALF_WIDTH, dirX: -1, dirY: -0.4 },
                                    { rimX: ARM_X + CUP_HALF_WIDTH, dirX: 1, dirY: -0.4 },
                                ];
                                return sides.map(function (_a) {
                                    var rimX = _a.rimX, dirX = _a.dirX, dirY = _a.dirY;
                                    var cx = rimX + travelFactor * dirX * AIR_SPREAD;
                                    var cy = cupMouthY + travelFactor * dirY * AIR_SPREAD;
                                    var tDx = airFlow === "out" ? dirX : -dirX;
                                    var tDy = airFlow === "out" ? dirY : -dirY;
                                    var angle = Math.atan2(tDy, tDx) * (180 / Math.PI);
                                    return (_jsx("g", { transform: "translate(".concat(cx, ", ").concat(cy, ") rotate(").concat(angle, ")"), opacity: opacity, children: _jsx("polygon", { points: "4,0 -2,-2 -2,2", fill: "white" }) }, "".concat(airFlow, "-").concat(i, "-").concat(dirX)));
                                });
                            })] })), _jsxs("defs", { children: [_jsxs("linearGradient", { id: "mountGradient", x1: "0%", y1: "0%", x2: "0%", y2: "100%", children: [_jsx("stop", { offset: "0%", stopColor: "#909090" }), _jsx("stop", { offset: "40%", stopColor: "#555555" }), _jsx("stop", { offset: "100%", stopColor: "#1c1c1c" })] }), _jsxs("linearGradient", { id: "myGradient", x1: "0%", y1: "0%", x2: "100%", y2: "100%", children: [_jsx("stop", { offset: "0%", stopColor: "#353535" }), _jsx("stop", { offset: "100%", stopColor: "#7B7B7B" })] })] }), _jsx("rect", { x: 10, y: OBJECT_Y + ATTACH_JUMP, width: OBJECT_WIDTH, height: OBJECT_HEIGHT, fill: "url(#myGradient)", opacity: 0.5, rx: 3 })] }) }));
};
